import { useStepperContext } from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  distributionRequestStatusEnum,
  legacyPermissionsControllerAtMeQueryOptions,
  useBrokerageFirmControllerCreateBrokerageFirm,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
} from 'korint-sdk';
import { Loader2Icon } from 'lucide-react';
import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';

/**
 * Module for creating a retail brokerage firm automatically
 *
 * This module exists to solve a permission issue: users without brokerage firm permissions
 * cannot access brokerage firm information pages or use functionalities like SIREN search.
 * By automatically creating a retail brokerage firm and associating the user as a broker,
 * we ensure users can proceed through the onboarding flow and access all required functionality.
 *
 * If the user already has a brokerage firm, checks distribution request status:
 * - PENDING/WAITING_FOR_SIGNATURE → redirects to /waiting
 * - APPROVED → redirects to / (dashboard)
 * - Otherwise → continues the funnel
 */
export function CreateRetailBrokerageFirmModule() {
  const { parentBrokerageFirmId } = useParams();
  const stepper = useStepperContext();

  const { data: user, isLoading: isUserLoading } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user?.brokerageFirmId;

  const { data: distributionRequests, isLoading: isDrLoading } = useQuery({
    ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
  });

  const { mutateAsync: createBrokerageFirm } = useBrokerageFirmControllerCreateBrokerageFirm();

  useQuery({
    queryKey: ['createBrokerageFirm', brokerageFirmId],
    queryFn: async () => {
      let firmId = brokerageFirmId;

      if (!firmId) {
        const { id } = await createBrokerageFirm({
          data: { firmType: 'RETAIL', parentBrokerageFirmId },
        });

        firmId = id;
      }

      stepper.next({
        parentBrokerageFirmId: parentBrokerageFirmId!,
        brokerageFirmId: firmId,
      });
    },
    enabled: !!user && !brokerageFirmId,
  });

  const latestDr = distributionRequests?.data?.[0];
  const isPendingOrWaiting =
    latestDr?.status === distributionRequestStatusEnum.PENDING ||
    latestDr?.status === distributionRequestStatusEnum.WAITING_FOR_SIGNATURE;
  const isApproved = latestDr?.status === distributionRequestStatusEnum.APPROVED;

  const shouldAdvance = !!brokerageFirmId && !isDrLoading && !isPendingOrWaiting && !isApproved;

  useEffect(() => {
    if (shouldAdvance) {
      stepper.next({
        parentBrokerageFirmId: parentBrokerageFirmId!,
        brokerageFirmId,
      });
    }
  }, [shouldAdvance, stepper, parentBrokerageFirmId, brokerageFirmId]);

  if (isUserLoading || (brokerageFirmId && isDrLoading)) {
    return <Loader2Icon className="size-8 animate-spin" />;
  }

  if (brokerageFirmId && isPendingOrWaiting) {
    return <Navigate to="/waiting" replace />;
  }

  if (brokerageFirmId && isApproved) {
    return <Navigate to="/" replace />;
  }

  return <Loader2Icon className="size-8 animate-spin" />;
}
